import { Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { 
    ConfirmationModalComponent, 
    ConfirmationModalData,
    ConfirmationModalType
} from '../components/confirmation-modal';

/**
 * ConfirmationService
 * 
 * Centralized service to manage confirmation modals across the app.
 * Provides pre-configured methods for common actions (delete, save, discard, etc).
 * 
 * @example
 * ```typescript
 * constructor(private confirmationService: ConfirmationService) {}
 * 
 * deleteUser(userId: string): void {
 *     this.confirmationService.confirmDelete('Usuario Juan Pérez')
 *         .subscribe(confirmed => {
 *             if (confirmed) {
 *                 this.userService.delete(userId).subscribe();
 *             }
 *         });
 * }
 * ```
 */
@Injectable({
    providedIn: 'root'
})
export class ConfirmationService {
    private dialog = inject(MatDialog);

    /**
     * Opens a generic confirmation modal
     * 
     * @param data Modal configuration
     * @returns Observable<boolean> - true if confirmed, false/undefined if cancelled
     */
    confirm(data: ConfirmationModalData): Observable<boolean> {
        // Convert customWidth to string if it's a number, or use default
        const width = data.customWidth 
            ? (typeof data.customWidth === 'number' ? `${data.customWidth}px` : data.customWidth)
            : '400px';

        const dialogRef: MatDialogRef<ConfirmationModalComponent, boolean> = this.dialog.open(
            ConfirmationModalComponent,
            {
                width: width,
                maxWidth: '90vw',
                disableClose: true, // Can only be closed with the action buttons
                data: data
            }
        );

        return dialogRef.afterClosed();
    }

    /**
     * Preconfigured modal to delete an item
     * 
     * @param itemName Name of the item to delete
     * @param additionalInfo Optional additional information
     * @returns Observable<boolean>
     * 
     * @example
     * ```typescript
     * this.confirmationService.confirmDelete('Usuario Juan Pérez')
     *     .subscribe(confirmed => {
     *         if (confirmed) {
     *             // Delete...
     *         }
     *     });
     * ```
     */
    confirmDelete(itemName: string, additionalInfo?: string): Observable<boolean> {
        let description = `¿Está seguro de eliminar <strong>${itemName}</strong>?`;
        
        if (additionalInfo) {
            description += `<br><br>${additionalInfo}`;
        } else {
            description += '<br><br><span class="text-red-600">Esta acción no se puede deshacer.</span>';
        }

        return this.confirm({
            title: 'Eliminar',
            description: description,
            type: ConfirmationModalType.DANGER,
            confirmButtonText: 'Eliminar',
            cancelButtonText: 'Cancelar',
            descriptionIcon: 'delete'
        });
    }

    /**
     * Preconfigured modal to void (anular) a document
     * 
     * @param documentName Name or identifier of the document
     * @returns Observable<boolean>
     */
    confirmAnular(documentName: string): Observable<boolean> {
        return this.confirm({
            title: 'Anular Documento',
            description: `¿Está seguro de anular el documento <strong>${documentName}</strong>?<br><br>Esta acción no se puede revertir.`,
            type: ConfirmationModalType.WARNING,
            confirmButtonText: 'Anular',
            cancelButtonText: 'Cancelar',
            descriptionIcon: 'cancel'
        });
    }

    /**
     * Preconfigured modal to save changes
     * 
     * @param message Optional custom message
     * @returns Observable<boolean>
     */
    confirmSave(message?: string): Observable<boolean> {
        return this.confirm({
            title: 'Guardar Cambios',
            description: message || '¿Desea guardar los cambios realizados?',
            type: ConfirmationModalType.SUCCESS,
            confirmButtonText: 'Guardar',
            cancelButtonText: 'Cancelar',
            descriptionIcon: 'save'
        });
    }

    /**
     * Preconfigured modal to discard changes
     * 
     * @returns Observable<boolean>
     */
    confirmDiscard(): Observable<boolean> {
        return this.confirm({
            title: 'Descartar Cambios',
            description: 'Tiene cambios sin guardar. ¿Está seguro de descartar los cambios?',
            type: ConfirmationModalType.WARNING,
            confirmButtonText: 'Descartar',
            cancelButtonText: 'Volver',
            descriptionIcon: 'warning'
        });
    }

    /**
     * Preconfigured modal for logout confirmation
     * 
     * @returns Observable<boolean>
     */
    confirmLogout(): Observable<boolean> {
        return this.confirm({
            title: 'Cerrar Sesión',
            description: '¿Está seguro de cerrar su sesión?',
            type: ConfirmationModalType.INFO,
            confirmButtonText: 'Cerrar Sesión',
            cancelButtonText: 'Cancelar',
            descriptionIcon: 'logout'
        });
    }

    /**
     * Preconfigured modal to send notifications
     * 
     * @param recipientCount Number of recipients
     * @returns Observable<boolean>
     */
    confirmSendNotification(recipientCount: number): Observable<boolean> {
        return this.confirm({
            title: 'Enviar Notificación',
            description: `Se enviará una notificación a <strong>${recipientCount} usuario(s)</strong>. ¿Desea continuar?`,
            type: ConfirmationModalType.INFO,
            confirmButtonText: 'Enviar',
            cancelButtonText: 'Cancelar',
            descriptionIcon: 'email'
        });
    }

    /**
     * Preconfigured modal to export data
     * 
     * @param dataType Type of data to export
     * @returns Observable<boolean>
     */
    confirmExport(dataType: string): Observable<boolean> {
        return this.confirm({
            title: 'Exportar Datos',
            description: `¿Está seguro de exportar los datos de <strong>${dataType}</strong>?<br><br><small>El proceso puede tardar varios minutos.</small>`,
            type: ConfirmationModalType.INFO,
            confirmButtonText: 'Exportar',
            cancelButtonText: 'Cancelar',
            descriptionIcon: 'file_download'
        });
    }

    /**
     * Preconfigured modal to confirm batch processing
     * 
     * @param batchSize Batch size
     * @returns Observable<boolean>
     */
    confirmBatchProcess(batchSize: number): Observable<boolean> {
        return this.confirm({
            title: 'Procesar Lote',
            description: `Se procesarán <strong>${batchSize} registros</strong>. Esta operación puede tardar varios minutos. ¿Desea continuar?`,
            type: ConfirmationModalType.INFO,
            confirmButtonText: 'Procesar',
            cancelButtonText: 'Cancelar',
            descriptionIcon: 'play_arrow',
            customWidth: '450px'
        });
    }

    /**
     * Preconfigured modal to restore default values
     * 
     * @returns Observable<boolean>
     */
    confirmReset(): Observable<boolean> {
        return this.confirm({
            title: 'Restaurar Valores',
            description: '¿Está seguro de restaurar los valores por defecto? Se perderán todas las configuraciones personalizadas.',
            type: ConfirmationModalType.WARNING,
            confirmButtonText: 'Restaurar',
            cancelButtonText: 'Cancelar',
            descriptionIcon: 'restore'
        });
    }

    /**
     * Preconfigured modal to activate/deactivate an item
     * 
     * @param itemName Name of the item
     * @param activate true to activate, false to deactivate
     * @returns Observable<boolean>
     */
    confirmToggleStatus(itemName: string, activate: boolean): Observable<boolean> {
        const action = activate ? 'activar' : 'desactivar';
        const icon = activate ? 'check_circle' : 'cancel';
        const type = activate ? ConfirmationModalType.SUCCESS : ConfirmationModalType.WARNING;

        return this.confirm({
            title: activate ? 'Activar' : 'Desactivar',
            description: `¿Está seguro de ${action} <strong>${itemName}</strong>?`,
            type: type,
            confirmButtonText: activate ? 'Activar' : 'Desactivar',
            cancelButtonText: 'Cancelar',
            descriptionIcon: icon
        });
    }

    /**
     * Generic warning modal
     * 
     * @param title The modal title
     * @param message Warning message
     * @returns Observable<boolean>
     */
    warning(title: string, message: string): Observable<boolean> {
        return this.confirm({
            title: title,
            description: message,
            type: ConfirmationModalType.WARNING,
            confirmButtonText: 'Entendido',
            cancelButtonText: 'Cancelar',
            descriptionIcon: 'warning'
        });
    }

    /**
     * Generic info modal that only requires confirmation (cancel button hidden)
     * 
     * @param title The modal title
     * @param message Informative message
     * @returns Observable<boolean>
     */
    info(title: string, message: string): Observable<boolean> {
        return this.confirm({
            title: title,
            description: message,
            type: ConfirmationModalType.INFO,
            confirmButtonText: 'Aceptar',
            cancelButtonText: '',  // Hide cancel button
            descriptionIcon: 'info'
        });
    }
}
